import React, { useEffect, useState } from 'react';
import './styles.css';

type StepDefinition = {
    /** Нижняя граница интервала (включительно) */
    from: number;
    /** Верхняя граница интервала (исключая значение to) */
    to: number;
    /** Шаг, который нужно использовать на данном интервале */
    step: number;
};

type RangeSliderProps = {
    /** Нижняя граница диапазона значений */
    min: number;
    /** Верхняя граница диапазона значений */
    max: number;
    /** Массив объектов, задающих зависимость шага от диапазона */
    stepConfig?: StepDefinition[];
    /** Текущее выбранное минимальное значение. Режим controlled */
    valueMin: number;
    /** Текущее выбранное максимальное значение. Режим controlled */
    valueMax: number;
    onChangeMin: (value: number) => void;
    onChangeMax: (value: number) => void;
    /** Дополнительный, минимальный разрыв между значениями (если требуется) */
    minGap?: number;
};

export const RangeSlider: React.FC<RangeSliderProps> = ({
                                                            min,
                                                            max,
                                                            stepConfig,
                                                            valueMin,
                                                            valueMax,
                                                            onChangeMin,
                                                            onChangeMax,
                                                            minGap = 0,
                                                        }) => {
    // Значения по умолчанию для stepConfig, если не переданы
    const defaultStepConfig: StepDefinition[] = [
        { from: 0, to: 100000, step: 10000 },
        { from: 100000, to: 500000, step: 50000 },
        { from: 500000, to: 2000000, step: 100000 },
        { from: 2000000, to: 30000000, step: 500000 },
    ];

    const config = stepConfig || defaultStepConfig;

    /**
     * Генерирует массив значений от min до max по заданной конфигурации.
     * Если min >= max, возвращает массив с единственным значением.
     * Если max выходит за диапазоны из config, расширяет их, чтобы общее число шагов в последнем диапазоне не превышало ~100.
     */
    const generateValueMap = (
        min: number,
        max: number,
        config: StepDefinition[]
    ): number[] => {
        if (min >= max) return [min];

        // Если верхнее значение max превышает to последнего диапазона, расширяем конфигурацию
        const extendedConfig = [...config];
        const lastConfig = extendedConfig[extendedConfig.length - 1];
        if (max > lastConfig.to) {
            // Рассчитываем шаг так, чтобы в диапазоне от lastConfig.to до max было примерно 100 шагов
            const computedStep = Math.max(1, Math.ceil((max - lastConfig.to) / 100));
            extendedConfig.push({
                from: lastConfig.to,
                to: max,
                step: computedStep,
            });
        }

        const values: number[] = [];
        let current = min;
        while (current < max) {
            values.push(current);
            // Находим диапазон, в котором лежит текущее значение
            const stepDef = extendedConfig.find(
                def => current >= def.from && current < def.to
            );
            // Если ни один диапазон не найден, используем шаг по умолчанию равный 1
            const step = stepDef ? stepDef.step : 1;
            if (step <= 0) {
                console.warn('Step value is non-positive. Exiting loop.');
                break;
            }
            const next = current + step;
            // Если следующий шаг не изменяет значение или перескакивает через max — завершаем цикл
            if (next <= current || next > max) break;
            current = next;
        }
        values.push(max); // гарантируем, что max включено
        return values;
    };

    const valueMap = generateValueMap(min, max, config);

    // Локальное состояние индексов выбранных значений в массиве valueMap
    const [sliderMinIndex, setSliderMinIndex] = useState(0);
    const [sliderMaxIndex, setSliderMaxIndex] = useState(valueMap.length - 1);

    // Находим индекс ближайшего значения в valueMap для заданного value
    const getIndexByValue = (val: number): number => {
        const index = valueMap.findIndex(v => v >= val);
        return index === -1 ? valueMap.length - 1 : index;
    };

    // При изменении входящих props (valueMin, valueMax или valueMap)
    // обновляем локальные индексы, чтобы положение бегунков соответствовало внешним данным.
    useEffect(() => {
        const newMinIndex = getIndexByValue(valueMin);
        const newMaxIndex = getIndexByValue(valueMax);
        setSliderMinIndex(newMinIndex);
        setSliderMaxIndex(newMaxIndex);
    }, [valueMin, valueMax, valueMap]);

    // При движении бегунков изменяем внешнее значение через onChange
    const handleChange = (index: number, type: 'min' | 'max') => {
        const val = valueMap[index];
        if (type === 'min') {
            // Левый бегунок обновляем только если выбранное значение меньше правого
            if (index < sliderMaxIndex && (valueMap[sliderMaxIndex] - val >= minGap)) {
                onChangeMin(val);
            }
        } else {
            if (index > sliderMinIndex && (val - valueMap[sliderMinIndex] >= minGap)) {
                onChangeMax(val);
            }
        }
    };

    return (
        <div>
            <div className="range-slider">
                <input
                    type="range"
                    min={0}
                    max={valueMap.length - 1}
                    value={sliderMinIndex}
                    onChange={e => handleChange(Number(e.target.value), 'min')}
                    className="thumb thumb-left"
                />
                <input
                    type="range"
                    min={0}
                    max={valueMap.length - 1}
                    value={sliderMaxIndex}
                    onChange={e => handleChange(Number(e.target.value), 'max')}
                    className="thumb thumb-right"
                />
                <div className="slider-track" />
                <div
                    className="slider-range"
                    style={{
                        left: `${(sliderMinIndex / (valueMap.length - 1)) * 100}%`,
                        width: `${((sliderMaxIndex - sliderMinIndex) / (valueMap.length - 1)) * 100}%`,
                    }}
                />
            </div>
            <div className="slider-labels">
                <span>{min.toLocaleString()}</span>
                <span>{max.toLocaleString()}</span>
            </div>
        </div>
    );
};
